import { l as DocContext, q as createFunctionFromDefinition, k as jsxRuntimeExports } from "./doenetml-a_KitGQm.js";
import React__default, { useContext, useRef, useEffect } from "react";
import { j as jxg, b as syncLabelMaskCssStyle, c as computeLabelMaskCssStyle, d as attachLabelHoverHighlight } from "./JSXGraphRenderer-BMkZb_8Q.js";
import { u as useDoenetRenderer } from "./useDoenetRenderer-Ca2HvWV4.js";
import { BoardContext, LINE_LAYER_OFFSET } from "./graph-gLVvVe_r.js";
import { g as getPatternFillAttributes } from "./fillPatterns-BQpfOUtY.js";
import { b as resolveCanvasColor } from "./styleColors-Cknqcr0q.js";
const regionBetweenCurveXAxis = React__default.memo(function RegionBetweenCurveXAxis(props) {
  let { id, SVs } = useDoenetRenderer(props);
  RegionBetweenCurveXAxis.ignoreActionsWithoutCore = () => true;
  const board = useContext(BoardContext);
  let curveJXG = useRef(null);
  let integralJXG = useRef(null);
  const { darkMode } = useContext(DocContext) || {};
  useEffect(() => {
    return () => {
      if (integralJXG.current !== null) {
        deleteRegion();
      }
    };
  }, []);
  function createRegion() {
    if (board === null) {
      return null;
    }
    if (!SVs.haveFunction || SVs.boundaryValues.length !== 2 || !SVs.boundaryValues.every(Number.isFinite)) {
      return null;
    }
    const fillAttributes = getPatternFillAttributes({
      defsEl: board.renderer.defs,
      boardId: board.container.id,
      fillStyle: SVs.selectedStyle.fillStyle ?? "solid",
      fillColor: darkMode === "dark" ? SVs.selectedStyle.fillColorDarkMode : SVs.selectedStyle.fillColor,
      fillOpacity: SVs.selectedStyle.fillOpacity,
      canvasColor: resolveCanvasColor(darkMode),
      fillPatternOpacity: SVs.selectedStyle.fillPatternOpacity
    });
    let jsxAttributes = {
      name: SVs.labelForGraph,
      visible: !SVs.hidden,
      withLabel: SVs.labelForGraph !== "",
      fixed: true,
      layer: 10 * SVs.layer + LINE_LAYER_OFFSET,
      fillColor: fillAttributes.fillColor,
      fillOpacity: fillAttributes.fillOpacity,
      highlight: false,
      // don't display points at left and right endpoints along function
      curveLeft: { visible: false },
      curveRight: { visible: false }
    };
    jsxAttributes.label = {
      highlight: false,
      ...computeLabelMaskCssStyle({
        layer: SVs.layer,
        masked: SVs.maskLabel
      }),
      highlightStrokeOpacity: 1
    };
    let f = createFunctionFromDefinition(SVs.fDefinition);
    curveJXG.current = board.create("functiongraph", f, {
      visible: false
    });
    const newIntegralJXG = board.create(
      "integral",
      [SVs.boundaryValues, curveJXG.current],
      jsxAttributes
    );
    attachLabelHoverHighlight({
      hoverTargetJXG: newIntegralJXG,
      getLabelJXG: () => integralJXG.current?.label,
      ...computeLabelMaskCssStyle({
        layer: SVs.layer,
        masked: SVs.maskLabel
      }),
      board
    });
    return newIntegralJXG;
  }
  function deleteRegion() {
    if (integralJXG.current) {
      board?.removeObject(integralJXG.current);
      integralJXG.current = null;
      if (curveJXG.current) {
        board?.removeObject(curveJXG.current);
        curveJXG.current = null;
      }
    }
  }
  if (board) {
    if (integralJXG.current === null) {
      integralJXG.current = createRegion();
    } else if (!SVs.haveFunction || SVs.boundaryValues.length !== 2 || !SVs.boundaryValues.every(Number.isFinite)) {
      deleteRegion();
    } else {
      let f = createFunctionFromDefinition(SVs.fDefinition);
      if (curveJXG.current) {
        curveJXG.current.Y = f;
      }
      integralJXG.current.visProp["visible"] = !SVs.hidden;
      integralJXG.current.visPropCalc["visible"] = !SVs.hidden;
      let [x1, x2] = SVs.boundaryValues;
      let [y1, y2] = SVs.boundaryValues.map(f);
      integralJXG.current.curveLeft.coords.setCoordinates(
        jxg.COORDS_BY_USER,
        [x1, y1]
      );
      integralJXG.current.curveRight.coords.setCoordinates(
        jxg.COORDS_BY_USER,
        [x2, y2]
      );
      let layer = 10 * SVs.layer + LINE_LAYER_OFFSET;
      let layerChanged = integralJXG.current.visProp.layer !== layer;
      if (layerChanged) {
        integralJXG.current.setAttribute({ layer });
      }
      const fillAttributes = getPatternFillAttributes({
        defsEl: board.renderer.defs,
        boardId: board.container.id,
        fillStyle: SVs.selectedStyle.fillStyle ?? "solid",
        fillColor: darkMode === "dark" ? SVs.selectedStyle.fillColorDarkMode : SVs.selectedStyle.fillColor,
        fillOpacity: SVs.selectedStyle.fillOpacity,
        canvasColor: resolveCanvasColor(darkMode),
        fillPatternOpacity: SVs.selectedStyle.fillPatternOpacity
      });
      if (integralJXG.current.visProp.fillcolor !== fillAttributes.fillColor) {
        integralJXG.current.visProp.fillcolor = fillAttributes.fillColor;
      }
      if (integralJXG.current.visProp.fillopacity !== fillAttributes.fillOpacity) {
        integralJXG.current.visProp.fillopacity = fillAttributes.fillOpacity;
      }
      if (integralJXG.current.hasLabel && integralJXG.current.label) {
        syncLabelMaskCssStyle(integralJXG.current.label, SVs.layer, {
          maskLabel: SVs.maskLabel
        });
      }
      integralJXG.current.curveLeft.needsUpdate = true;
      integralJXG.current.curveLeft.update();
      integralJXG.current.curveLeft.fullUpdate();
      integralJXG.current.curveRight.needsUpdate = true;
      integralJXG.current.curveLeft.update();
      integralJXG.current.curveRight.fullUpdate();
      integralJXG.current.needsUpdate = true;
      integralJXG.current.curveLeft.update();
      integralJXG.current.fullUpdate();
      board.update();
      board.fullUpdate();
      board.updateRenderer();
    }
    return /* @__PURE__ */ jsxRuntimeExports.jsx(jsxRuntimeExports.Fragment, { children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { id }) });
  }
  if (SVs.hidden) {
    return null;
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsx(jsxRuntimeExports.Fragment, { children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { id }) });
});
export {
  regionBetweenCurveXAxis as default
};
